import React from 'react';
import {
  cn,
  formatConfidencePercent,
  formatCurrency,
  formatDate,
  formatDateWithPrecision,
  formatRelativeTime,
  formatRoundLabel,
  getStageColor,
  getScoreColor,
  truncate,
} from '../../lib/utils';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertCircle,
  FileText,
  Loader2,
  ShieldCheck,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';

const USER_STATE_META = {
  unseen: {
    label: 'Unseen',
    className: 'text-zinc-300 bg-zinc-800/80 border-zinc-700',
  },
  seen: {
    label: 'Seen',
    className: 'text-zinc-300 bg-zinc-800/80 border-zinc-700',
  },
  pass: {
    label: 'Pass',
    className: 'text-rose-300 bg-rose-400/10 border-rose-400/20',
  },
  shortlist: {
    label: 'Shortlist',
    className: 'text-amber-300 bg-amber-400/10 border-amber-400/20',
  },
  deep_diligence: {
    label: 'Deep Diligence',
    className: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/20',
  },
};

export const getUserStartupStateMeta = (state) => {
  if (!state) {
    return USER_STATE_META.unseen;
  }
  return USER_STATE_META[state] || USER_STATE_META.seen;
};

export const StartupCard = ({ startup, onClick, compact = false, actions = [], onScoreClick }) => {
  const convictionScore = startup.conviction_score?.total_score ?? startup.conviction_score ?? 0;
  const userState = startup.user_startup_state?.state || startup.state || startup.review_state;
  const stateMeta = getUserStartupStateMeta(userState);
  const stateTimestamp =
    startup.user_startup_state?.last_opened_at ||
    startup.user_startup_state?.seen_at ||
    startup.user_startup_state?.state_changed_at ||
    startup.updated_at ||
    startup.created_at;
  const latestDeckValue = startup.latest_deck || startup.latest_deck_url || null;
  const latestDeckUrl = typeof latestDeckValue === 'string'
    ? latestDeckValue
    : latestDeckValue?.url || startup.latest_deck_url || null;
  const latestDeckTitle = typeof latestDeckValue === 'string'
    ? 'Latest deck'
    : latestDeckValue?.title || latestDeckValue?.deck_title || 'Latest deck';
  const latestFundingRound = startup.latest_funding_round || null;
  const latestFundingDate = latestFundingRound?.date
    ? formatDateWithPrecision(latestFundingRound.date, latestFundingRound.date_precision)
    : null;
  const founderPreview = Array.isArray(startup.founder_preview) ? startup.founder_preview : [];
  const founderSummary = founderPreview
    .map((founder) => founder?.name)
    .filter(Boolean)
    .join(' • ');
  const trustSummary = startup.trust_summary || null;
  const trustLabel = trustSummary?.confidence_percent ? `Trust ${trustSummary.confidence_percent}%` : null;

  return (
    <div
      onClick={onClick}
      data-testid={`startup-card-${startup.startup_id}`}
      className={cn(
        "bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer transition-all duration-200",
        "hover:border-zinc-600 hover:bg-zinc-900/80",
        compact && "p-3"
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-zinc-800 rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden">
          {startup.logo_url ? (
            <img src={startup.logo_url} alt={startup.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-zinc-500 font-mono text-sm">{startup.name?.charAt(0)}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0">
              <h3 className="text-zinc-50 font-medium font-['Outfit'] truncate">{startup.name}</h3>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className={cn("px-2 py-0.5 text-[10px] font-mono rounded-full border", stateMeta.className)}>
                  {stateMeta.label}
                </span>
                {startup.watchlisted && (
                  <span className="px-2 py-0.5 text-[10px] font-mono text-blue-300 bg-blue-400/10 rounded-full border border-blue-400/20">
                    Watchlist
                  </span>
                )}
              </div>
            </div>

            {convictionScore > 0 && (
              onScoreClick ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onScoreClick(startup);
                  }}
                  className={cn(
                    "flex items-center gap-1 font-mono text-sm rounded-full px-2 py-1 transition-colors hover:bg-zinc-800",
                    getScoreColor(convictionScore),
                  )}
                  data-testid={`startup-score-open-${startup.startup_id}`}
                >
                  <Zap className="w-3.5 h-3.5" />
                  <span>{Math.round(convictionScore)}</span>
                </button>
              ) : (
                <div className={cn("flex items-center gap-1 font-mono text-sm", getScoreColor(convictionScore))}>
                  <Zap className="w-3.5 h-3.5" />
                  <span>{Math.round(convictionScore)}</span>
                </div>
              )
            )}
          </div>

          {!compact && startup.description && (
            <p className="text-zinc-400 text-sm font-['Manrope'] mb-2 line-clamp-2">
              {truncate(startup.description, 120)}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {startup.stage && (
              <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full border", getStageColor(startup.stage))}>
                {startup.stage}
              </span>
            )}
            {startup.sector && (
              <span className="px-2 py-0.5 text-xs font-mono text-zinc-400 bg-zinc-800 rounded-full">
                {startup.sector}
              </span>
            )}
            {startup.total_funding > 0 && (
              <span className="text-xs font-mono text-zinc-500">
                {formatCurrency(startup.total_funding)} raised
              </span>
            )}
            {latestFundingRound?.round_type && (
              <span className="px-2 py-0.5 text-xs font-mono text-emerald-300 bg-emerald-400/10 rounded-full border border-emerald-400/20">
                {formatRoundLabel(latestFundingRound.round_type)}
                {latestFundingDate ? ` • ${latestFundingDate}` : ''}
              </span>
            )}
            {trustLabel && (
              <span className="px-2 py-0.5 text-xs font-mono text-zinc-300 bg-zinc-800 rounded-full border border-zinc-700">
                {trustLabel}
              </span>
            )}
          </div>

          {!compact && (founderSummary || trustSummary?.signals?.length > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {founderSummary && (
                <span className="flex items-center gap-1 text-zinc-300">
                  <Users className="w-3.5 h-3.5 text-zinc-500" />
                  {founderSummary}
                </span>
              )}
              {trustSummary?.signals?.slice(0, 2).map((signal) => (
                <span
                  key={signal.key || signal.label}
                  className="text-zinc-500 font-mono"
                >
                  {signal.label}
                </span>
              ))}
            </div>
          )}

          {!compact && latestDeckUrl && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-400">
              <FileText className="w-3.5 h-3.5 flex-shrink-0" />
              <a href={latestDeckUrl} target="_blank" rel="noopener noreferrer" className="truncate hover:text-amber-300">
                {latestDeckTitle}
              </a>
            </div>
          )}
        </div>
      </div>

      {!compact && (startup.last_signal || stateTimestamp) && (
        <div className="mt-3 pt-3 border-t border-zinc-800/50 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <TrendingUp className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
            <span className="text-zinc-400 text-xs font-mono truncate">
              {startup.last_signal?.title || 'No signal yet'}
            </span>
          </div>
          {stateTimestamp && (
            <span className="text-zinc-500 text-[10px] font-mono flex-shrink-0">
              {userState === 'seen' ? `Seen ${formatRelativeTime(stateTimestamp)}` : formatRelativeTime(stateTimestamp)}
            </span>
          )}
        </div>
      )}

      {actions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-800/50 flex flex-wrap gap-2">
          {actions.map((action) => (
            <Button
              key={action.key || action.label}
              size="sm"
              variant={action.variant || 'outline'}
              disabled={action.disabled}
              onClick={(event) => {
                event.stopPropagation();
                action.onClick?.(event);
              }}
              data-testid={action.testid || `startup-action-${startup.startup_id}-${action.key || action.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={action.className}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export const StartupCardSkeleton = ({ compact = false }) => (
  <div className={cn("bg-zinc-900 border border-zinc-800 rounded-md p-4 animate-pulse", compact && "p-3")}>
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 bg-zinc-800 rounded-md" />
      <div className="flex-1">
        <div className="h-5 w-32 bg-zinc-800 rounded mb-2" />
        {!compact && <div className="h-4 w-full bg-zinc-800 rounded mb-2" />}
        <div className="flex gap-2">
          <div className="h-5 w-16 bg-zinc-800 rounded-full" />
          <div className="h-5 w-20 bg-zinc-800 rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

export const InvestorCard = ({ investor, onClick }) => {
  return (
    <div
      onClick={onClick}
      data-testid={`investor-card-${investor.investor_id}`}
      className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer transition-all duration-200 hover:border-zinc-600"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
          {investor.profile_image ? (
            <img src={investor.profile_image} alt={investor.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-zinc-500 font-mono text-sm">{investor.name?.charAt(0)}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-zinc-50 font-medium font-['Outfit'] truncate">{investor.name}</h3>
          <p className="text-zinc-400 text-sm font-['Manrope']">
            {investor.title} {investor.firm && `@ ${investor.firm}`}
          </p>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="px-2 py-0.5 text-xs font-mono text-blue-400 bg-blue-400/10 rounded-full border border-blue-400/20">
              {investor.investor_type}
            </span>
            {investor.sector_preferences?.slice(0, 2).map((sector, i) => (
              <span key={i} className="px-2 py-0.5 text-xs font-mono text-zinc-400 bg-zinc-800 rounded-full">
                {sector}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SignalItem = ({ signal, startupName }) => {
  const getSignalColor = (type) => {
    switch (type) {
      case 'funding':
        return 'text-emerald-400 bg-emerald-400/10';
      case 'hiring':
        return 'text-blue-400 bg-blue-400/10';
      case 'product_launch':
        return 'text-amber-400 bg-amber-400/10';
      case 'news':
        return 'text-rose-400 bg-rose-400/10';
      default:
        return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-md border border-zinc-800/50">
      <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0", getSignalColor(signal.signal_type))}>
        <TrendingUp className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {startupName && (
            <span className="text-zinc-50 font-medium text-sm font-['Outfit']">{startupName}</span>
          )}
          <span className="text-zinc-500 text-xs font-mono">{signal.signal_type}</span>
        </div>
        <p className="text-zinc-400 text-sm font-['Manrope'] line-clamp-2">{signal.title}</p>
        <p className="text-zinc-500 text-xs font-mono mt-1">{signal.source}</p>
      </div>
    </div>
  );
};

const METRIC_LABELS = {
  opportunity_score: 'Opportunity',
  timing_score: 'Timing',
  founder_score: 'Founder',
  product_strength_score: 'Product',
  market_inevitability_score: 'Market',
  fundability_score: 'Fundability',
  non_consensus_alpha_score: 'Alpha',
  introability_score: 'Intro',
};

const DETAIL_LABELS = {
  tailwinds: 'Tailwinds',
  headwinds: 'Headwinds',
  opportunities: 'Opportunities',
  risks: 'Risks',
  assumptions: 'Assumptions',
  strengths: 'Strengths',
  gaps: 'Gaps',
  comparables: 'Comparables',
  wedge: 'Wedge',
  platform_signals: 'Platform signals',
  investor_signals: 'Investor signals',
  missing_signals: 'Missing signals',
};

export const ScoreDrilldownDialog = ({
  open,
  onOpenChange,
  startupName,
  drilldown,
  loading = false,
  error = null,
  onRetry,
}) => {
  const rankingSummary = drilldown?.ranking_summary || null;
  const metrics = drilldown?.metrics || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl">
            Diligence Trace
            {startupName ? <span className="text-zinc-400 font-normal"> • {startupName}</span> : null}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-400">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Loading persisted diligence reasoning...
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="text-zinc-200">Could not load the diligence trace.</p>
            <p className="text-zinc-500 text-sm mt-1">{error}</p>
            {onRetry ? (
              <Button
                onClick={onRetry}
                variant="outline"
                className="mt-4 border-zinc-700 text-zinc-300"
              >
                Retry
              </Button>
            ) : null}
          </div>
        ) : !drilldown?.available ? (
          <div className="py-12 text-center">
            <Zap className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-200">No persisted diligence trace yet.</p>
            <p className="text-zinc-500 text-sm mt-1">Run diligence to unlock the per-agent reasoning view.</p>
          </div>
        ) : (
          <div className="space-y-6 max-h-[72vh] overflow-y-auto pr-1">
            <div className="grid gap-3 md:grid-cols-[minmax(0,2fr),minmax(280px,1fr)]">
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="font-['Outfit'] text-lg">Ranking Summary</h3>
                  <div className="text-right">
                    <p className={cn("font-mono text-lg", getScoreColor(drilldown.total_score || 0))}>
                      {Math.round(drilldown.total_score || 0)}
                    </p>
                    {drilldown.display_score ? (
                      <p className="text-zinc-500 text-xs font-mono">display {drilldown.display_score}</p>
                    ) : null}
                  </div>
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  {rankingSummary?.summary || 'No ranking summary was persisted for this diligence run.'}
                </p>

                {(rankingSummary?.key_strengths?.length > 0 || rankingSummary?.key_risks?.length > 0) && (
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div>
                      <p className="text-zinc-500 text-xs font-mono mb-2">Key Strengths</p>
                      <div className="flex flex-wrap gap-2">
                        {rankingSummary?.key_strengths?.map((item) => (
                          <span key={item} className="px-2 py-1 rounded-full text-xs bg-emerald-400/10 text-emerald-300 border border-emerald-400/20">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs font-mono mb-2">Key Risks</p>
                      <div className="flex flex-wrap gap-2">
                        {rankingSummary?.key_risks?.map((item) => (
                          <span key={item} className="px-2 py-1 rounded-full text-xs bg-rose-400/10 text-rose-300 border border-rose-400/20">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                  <p className="text-zinc-500 text-xs font-mono mb-1">Trace Quality</p>
                  <p className="text-zinc-50 font-medium capitalize">{drilldown.trace_mode?.replace(/_/g, ' ') || 'Unknown'}</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                  <p className="text-zinc-500 text-xs font-mono mb-1">Ranking Confidence</p>
                  <p className="text-zinc-50 font-medium">
                    {formatConfidencePercent(rankingSummary?.confidence) || 'Not captured'}
                  </p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                  <p className="text-zinc-500 text-xs font-mono mb-1">Generated</p>
                  <p className="text-zinc-50 font-medium">
                    {drilldown.generated_at ? formatDate(drilldown.generated_at) : 'Not captured'}
                  </p>
                </div>
              </div>
            </div>

            {Object.keys(metrics).length > 0 && (
              <div className="grid gap-3 md:grid-cols-2">
                {drilldown.metric_order?.map((metricName) => {
                  const metric = metrics[metricName];
                  if (!metric) return null;
                  return (
                    <div key={metricName} className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-zinc-200 font-medium">{METRIC_LABELS[metricName] || metricName}</p>
                        <div className="text-right">
                          <p className={cn("font-mono", getScoreColor(metric.score || 0))}>
                            {Math.round(metric.score || 0)}
                          </p>
                          {metric.confidence ? (
                            <p className="text-zinc-500 text-[11px] font-mono">
                              {formatConfidencePercent(metric.confidence)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {metric.explanation ? (
                        <p className="text-zinc-400 text-sm leading-relaxed">{metric.explanation}</p>
                      ) : null}
                      {metric.source_agents?.length > 0 ? (
                        <p className="text-zinc-600 text-xs font-mono mt-2">
                          {metric.source_agents.join(' • ')}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-['Outfit'] text-lg">Per-Agent Reasoning</h3>
                {drilldown.trace_mode !== 'full' ? (
                  <span className="text-zinc-500 text-xs font-mono">summary only</span>
                ) : null}
              </div>

              {drilldown.agent_reasoning?.length > 0 ? (
                drilldown.agent_reasoning.map((agent) => (
                  <div key={agent.agent} className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="text-zinc-50 font-medium">{agent.label}</p>
                        {agent.summary ? (
                          <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{agent.summary}</p>
                        ) : null}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={cn("font-mono", getScoreColor(agent.score || 0))}>
                          {Math.round(agent.score || 0)}
                        </p>
                        {agent.confidence ? (
                          <p className="text-zinc-500 text-[11px] font-mono">
                            {formatConfidencePercent(agent.confidence)}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {agent.evidence?.length > 0 ? (
                      <div className="mb-3">
                        <p className="text-zinc-500 text-xs font-mono mb-1">Evidence</p>
                        <div className="flex flex-wrap gap-2">
                          {agent.evidence.map((item) => (
                            <span key={item} className="px-2 py-1 rounded-full text-xs bg-zinc-800 text-zinc-300 border border-zinc-700">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {Object.entries(agent.details || {}).length > 0 ? (
                      <div className="grid gap-3 md:grid-cols-2">
                        {Object.entries(agent.details).map(([detailKey, items]) => (
                          <div key={detailKey}>
                            <p className="text-zinc-500 text-xs font-mono mb-1">
                              {DETAIL_LABELS[detailKey] || detailKey}
                            </p>
                            <div className="space-y-1">
                              {items.map((item) => (
                                <p key={item} className="text-zinc-400 text-sm leading-relaxed">
                                  {item}
                                </p>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6 text-center">
                  <ShieldCheck className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-zinc-300">Only the ranking summary was persisted for this run.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ScoreBreakdown = ({ scores }) => {
  if (!scores) return null;

  const scoreItems = [
    { key: 'opportunity_score', label: 'Opportunity' },
    { key: 'timing_score', label: 'Timing' },
    { key: 'founder_score', label: 'Founder' },
    { key: 'product_strength_score', label: 'Product' },
    { key: 'market_inevitability_score', label: 'Market' },
    { key: 'fundability_score', label: 'Fundability' },
    { key: 'non_consensus_alpha_score', label: 'Alpha' },
    { key: 'introability_score', label: 'Intro-ability' },
  ];

  return (
    <div className="space-y-3">
      {scoreItems.map((item) => {
        const value = typeof scores[item.key] === 'object'
          ? scores[item.key]?.score || 0
          : scores[item.key] || 0;

        return (
          <div key={item.key}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-400 text-xs font-mono">{item.label}</span>
              <span className={cn("text-xs font-mono", getScoreColor(value))}>{Math.round(value)}</span>
            </div>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  value >= 80 ? 'bg-emerald-400' :
                  value >= 60 ? 'bg-blue-400' :
                  value >= 40 ? 'bg-amber-400' : 'bg-rose-400'
                )}
                style={{ width: `${value}%` }}
              />
            </div>
            {typeof scores[item.key] === 'object' && scores[item.key]?.explanation && (
              <p className="text-zinc-500 text-xs mt-2 leading-relaxed">
                {scores[item.key].explanation}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
